import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormControl,
  Menu,
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import { changePageAction } from 'store/actions/dashboardActions';
import { useAppState } from 'store/Provider';
import { useSelector } from 'store/hooks';

interface SimpleIdNameOption {
  id: number;
  name: string;
}

type ISearchHeaderCellOptions = SimpleIdNameOption[];

type FilterDropdownCellProps = {
  field: string;
  options?: ISearchHeaderCellOptions; // [{ id: 1, name: "Missing Submission" }]
  headerName: string;
  disabled?: boolean;
};

const FilterDropdown = React.memo((props: FilterDropdownCellProps) => {
  const { field, options = [], headerName, disabled = false } = props;
  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [checkedOptions, setCheckedOptions] = useState<Record<number, boolean>>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const { filter } = useSelector((state) => state);

  // initialize checkbox states to UNCHECKED on first load
  useEffect(() => {
    const initialState: Record<number, boolean> = {};
    const filterValue = filter?.selected?.[field]?.[0] ?? '';
    let selectedNames: string[] = [];

    if (filterValue === 'ALL') {
      // If ALL, check all options
      options.forEach((opt) => {
        initialState[opt.id] = true;
      });
      setIsSelectAllChecked(true);
      setSelectedFilter('ALL');
    } else {
      selectedNames = filterValue
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);

      options.forEach((opt) => {
        initialState[opt.id] = selectedNames.includes(opt.name);
      });
      setIsSelectAllChecked(selectedNames.length === options.length && options.length > 0);
      setSelectedFilter(filterValue);
    }
    setCheckedOptions(initialState);
  }, [options]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // setIsSelectAllChecked(false);
    // setSelectedFilter('');
  };

  const updateSelectAllState = (newCheckedOptions: Record<number, boolean>) => {
    // Check if every option is selected
    const allOptionsSelected = options.length > 0 && options.every((opt) => newCheckedOptions[opt.id]);
    setIsSelectAllChecked(allOptionsSelected);
  };

  const handleCheckboxChange = (id: number, checked: boolean): void => {
    setCheckedOptions((prev) => {
      const newCheckedOptions = {
        ...prev,
        [id]: checked,
      };
      updateSelectAllState(newCheckedOptions);
      return newCheckedOptions;
    });
  };

  const handleSelectAllChange = (checked: boolean): void => {
    setIsSelectAllChecked(checked);
    setCheckedOptions(() => {
      const newCheckedOptions: Record<number, boolean> = {};
      options.forEach((opt) => {
        newCheckedOptions[opt.id] = checked;
      });
      return newCheckedOptions;
    });
  };

  const handleApplyClick = (): void => {
    // Determine which filter string to display
    if (isSelectAllChecked || options.length === 0) {
      dispatch(selectFilterAction({ [field]: ['ALL'] }));
      setSelectedFilter('ALL');
    } else {
      // Instead of a single comma-separated string, use an array of names
      // Output as JSON string inside array for API: ["Missing Assessment","Missing OTP","Missing Report Signature"]
      const selectedNamesArr = options.filter((opt) => checkedOptions[opt.id]).map((opt) => opt.name);
      const selectedNamesJson = JSON.stringify(selectedNamesArr);
      const selectedNames = selectedNamesArr.join(', ');

      if (selectedNamesArr.length === 0) {
        setSelectedFilter('');
        dispatch(selectFilterAction({ [field]: [] }));
      } else {
        // Pass the array of names directly as required by SelectedFiltersList
        dispatch(selectFilterAction({ [field]: [selectedNamesJson] }));
        setSelectedFilter(selectedNames);
      }
    }

    handleClose();
  };

  const handleClear = (): void => {
    // Reset to "All" (all options checked)
    dispatch(selectFilterRemoveAction({ [field]: [] }));
    handleSelectAllChange(false);
    setSelectedFilter('');
    if (field === 'type') {
      dispatch(changePageAction(0));
    }
    setOpen(false);
  };

  // Function to stop propagation, preventing the menu from closing
  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box sx={{ display: 'block', width: '100%' }}>
      <FormControl fullWidth>
        <label htmlFor={`select_search_${field}`}>{t(headerName)}</label>

        <TextField
          value={selectedFilter}
          onClick={handleClick}
          disabled={disabled}
          fullWidth
          variant="outlined"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {selectedFilter && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    size="small"
                    sx={{ zIndex: 1, mr: 1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton onClick={handleClick} size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
                {/* )} */}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingRight: '30px',
              height: '32px',
              // padding: '0 8px',
            },
            '& .MuiInputBase-root': {
              padding: '2px !important',
              height: '32px',
            },
            '& .MuiInputLabel-root': {
              top: '-2px',
            },
          }}
        />
      </FormControl>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `select_search_${field}`,
          disablePadding: true,
          disableListWrap: true,
        }}
      >
        {options.length === 0 ? (
          <MenuItem disabled sx={{ justifyContent: 'center', color: 'gray' }}>
            No data found
          </MenuItem>
        ) : (
          <>
            {/* --- 'All' Option --- */}
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ paddingY: 0, paddingX: 1, borderBottom: '1px solid #ccc' }}
              // **APPLYING THE FIX HERE:** Use onMouseDown to stop propagation when interacting with this menu item
              onMouseDown={handleMenuClick}
            >
              <FormControlLabel
                control={
                  <Checkbox checked={isSelectAllChecked} onChange={(e) => handleSelectAllChange(e.target.checked)} />
                }
                label="ALL" // Hardcoded 'All' label
                sx={{ width: '100%', m: 0, fontWeight: 'bold' }}
              />
            </MenuItem>
            {/* --- Individual Options --- */}
            {options.map((opt) => (
              <MenuItem
                key={opt.id}
                disableRipple
                disableTouchRipple
                sx={{ paddingY: 0, paddingX: 1 }}
                // **APPLYING THE FIX HERE:** Use onMouseDown to stop propagation
                onMouseDown={handleMenuClick}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!checkedOptions[opt.id]}
                      onChange={(e) => handleCheckboxChange(opt.id, e.target.checked)}
                    />
                  }
                  label={opt.name}
                  sx={{ width: '100%', m: 0 }}
                />
              </MenuItem>
            ))}
            {/* --- Action Buttons --- */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', p: '10px' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleApplyClick}>
                Apply
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </Box>
  );
});

export default FilterDropdown;
